import { useState, useEffect } from 'react';
import { appliedApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
    Briefcase, ArrowLeft, Loader2, Trash2, ExternalLink, 
    Search, CheckCircle, XCircle, Clock, AlertCircle, Edit2, Save
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const STATUS_CONFIG = {
    applied: { label: 'Applied', icon: Clock, color: 'text-muted-foreground', bg: 'bg-muted-foreground/10' },
    interviewing: { label: 'Interviewing', icon: AlertCircle, color: 'text-cyber-purple', bg: 'bg-cyber-purple/10' },
    offered: { label: 'Offered', icon: CheckCircle, color: 'text-acid', bg: 'bg-acid/10' },
    rejected: { label: 'Rejected', icon: XCircle, color: 'text-neon-red', bg: 'bg-neon-red/10' }
};

export default function AppliedPage() {
    const [applied, setApplied] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editNotes, setEditNotes] = useState('');

    useEffect(() => {
        loadApplied();
    }, []);

    const loadApplied = async () => {
        try {
            const data = await appliedApi.list();
            setApplied(data);
        } catch (error) {
            console.error('Error loading applied jobs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = async (appliedId, newStatus) => {
        try {
            await appliedApi.update(appliedId, { status: newStatus });
            setApplied(prev => prev.map(a => 
                a.applied_id === appliedId ? { ...a, status: newStatus } : a
            ));
            toast.success('Status updated');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleSaveNotes = async (appliedId) => {
        try {
            await appliedApi.update(appliedId, { notes: editNotes });
            setApplied(prev => prev.map(a => 
                a.applied_id === appliedId ? { ...a, notes: editNotes } : a
            ));
            setEditingId(null);
            toast.success('Notes saved');
        } catch (error) {
            toast.error('Failed to save notes');
        }
    };

    const handleRemove = async (appliedId) => {
        try {
            await appliedApi.remove(appliedId);
            setApplied(prev => prev.filter(a => a.applied_id !== appliedId));
            toast.success('Removed from applied jobs');
        } catch (error) {
            toast.error('Failed to remove');
        }
    };

    const groupedApplied = {
        interviewing: applied.filter(a => a.status === 'interviewing'),
        applied: applied.filter(a => a.status === 'applied'),
        offered: applied.filter(a => a.status === 'offered'),
        rejected: applied.filter(a => a.status === 'rejected')
    };

    return (
        <div className="min-h-screen bg-void page-enter">
            <div className="noise-overlay" />

            {/* Header */}
            <header className="sticky top-0 z-20 glass border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Link 
                        to="/dashboard" 
                        className="text-muted-foreground hover:text-acid transition-colors"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                    </Link>
                    <h1 className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-cyber-purple" strokeWidth={1.5} />
                        Applied Jobs
                    </h1>
                    {applied.length > 0 && (
                        <Badge className="ml-auto font-mono text-xs bg-cyber-purple/10 text-cyber-purple border-cyber-purple/30">
                            {applied.length} applications
                        </Badge>
                    )}
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8">
                {isLoading ? (
                    <div className="text-center py-20">
                        <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground text-sm">Loading applications...</p>
                    </div>
                ) : applied.length === 0 ? (
                    <Card className="bg-void-paper border-border">
                        <CardContent className="text-center py-20">
                            <Briefcase className="w-16 h-16 text-muted-foreground/30 mx-auto mb-6" strokeWidth={1.5} />
                            <h2 className="font-display text-xl font-bold text-white mb-2">
                                No applications yet
                            </h2>
                            <p className="text-muted-foreground text-sm mb-6">
                                Start applying to jobs and track your progress here
                            </p>
                            <Link to="/dashboard">
                                <Button className="bg-acid text-black font-mono uppercase tracking-wider text-sm hover:bg-acid-hover">
                                    <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Find Jobs
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-8">
                        {/* Status Stats */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                                const Icon = config.icon;
                                const count = groupedApplied[status]?.length || 0;
                                return (
                                    <Card key={status} className="bg-void-paper border-border">
                                        <CardContent className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 ${config.bg} rounded-sm flex items-center justify-center`}>
                                                    <Icon className={`w-5 h-5 ${config.color}`} strokeWidth={1.5} />
                                                </div>
                                                <div>
                                                    <div className="font-display text-2xl font-bold text-white">{count}</div>
                                                    <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{config.label}</div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Applications List */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg">All Applications</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ScrollArea className="h-[600px]">
                                    <div className="space-y-4">
                                        {applied.map((app) => {
                                            const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.applied;
                                            const StatusIcon = statusConfig.icon;
                                            
                                            return (
                                                <div 
                                                    key={app.applied_id}
                                                    className="p-5 bg-void-subtle border border-border rounded-sm card-hover group"
                                                    data-testid={`applied-${app.applied_id}`}
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                                <h3 className="font-display text-base font-bold text-white group-hover:text-acid transition-colors">
                                                                    {app.job.title}
                                                                </h3>
                                                                <Badge className={`font-mono text-xs ${statusConfig.color} border-current ${statusConfig.bg}`}>
                                                                    <StatusIcon className="w-3 h-3 mr-1" strokeWidth={1.5} />
                                                                    {statusConfig.label}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-muted-foreground text-sm font-medium mb-2">
                                                                {app.job.company}
                                                            </p>
                                                            <p className="text-muted-foreground/50 text-xs font-mono mb-3">
                                                                Applied {new Date(app.applied_at).toLocaleDateString()}
                                                            </p>
                                                            
                                                            {/* Notes Section */}
                                                            {editingId === app.applied_id ? (
                                                                <div className="mt-3 space-y-2">
                                                                    <Textarea
                                                                        value={editNotes}
                                                                        onChange={(e) => setEditNotes(e.target.value)}
                                                                        placeholder="Add notes about this application..."
                                                                        rows={3}
                                                                        className="bg-black/20 border-border focus:border-acid text-sm resize-none"
                                                                        data-testid={`notes-input-${app.applied_id}`}
                                                                    />
                                                                    <div className="flex gap-2">
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleSaveNotes(app.applied_id)}
                                                                            className="bg-acid text-black font-mono text-xs hover:bg-acid-hover"
                                                                        >
                                                                            <Save className="w-3 h-3 mr-1" strokeWidth={1.5} />
                                                                            Save
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            onClick={() => setEditingId(null)}
                                                                            className="text-muted-foreground font-mono text-xs"
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : app.notes ? (
                                                                <div 
                                                                    className="mt-3 p-3 bg-black/20 rounded-sm border border-border cursor-pointer hover:border-acid/30 transition-colors"
                                                                    onClick={() => {
                                                                        setEditingId(app.applied_id);
                                                                        setEditNotes(app.notes);
                                                                    }}
                                                                >
                                                                    <p className="text-muted-foreground text-sm">{app.notes}</p>
                                                                </div>
                                                            ) : (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setEditingId(app.applied_id);
                                                                        setEditNotes('');
                                                                    }}
                                                                    className="mt-2 text-muted-foreground/50 hover:text-acid text-xs font-mono"
                                                                >
                                                                    <Edit2 className="w-3 h-3 mr-1" strokeWidth={1.5} />
                                                                    Add notes
                                                                </Button>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="flex flex-col gap-2 items-end">
                                                            <Select
                                                                value={app.status}
                                                                onValueChange={(value) => handleStatusChange(app.applied_id, value)}
                                                            >
                                                                <SelectTrigger 
                                                                    className="w-[140px] h-8 bg-black/20 border-border text-xs font-mono"
                                                                    data-testid={`status-select-${app.applied_id}`}
                                                                >
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent className="bg-void-paper border-border">
                                                                    {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                                                                        <SelectItem 
                                                                            key={status} 
                                                                            value={status}
                                                                            className="font-mono text-xs"
                                                                        >
                                                                            {config.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            
                                                            <div className="flex gap-1">
                                                                <a
                                                                    href={app.job.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-neon-cyan transition-colors"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                                                </a>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleRemove(app.applied_id)}
                                                                    className="text-muted-foreground hover:text-neon-red"
                                                                    data-testid={`remove-${app.applied_id}`}
                                                                >
                                                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </main>
        </div>
    );
}
